import { css } from '@emotion/react';
import {
  type FC, type PropsWithChildren, type Ref, useEffect, useRef,
} from 'react';
import { AlertComponent } from '~/_shared/baseComponents/alert';
import { SPREADSHEET_ERROR_TIMEOUT } from '~/_shared/components/spreadsheet/constants';
import { useHookWithRefCallback } from '../../../utils/hooks/useHookWithRefCallback';
import { FixedStickyContainerComponent } from '../../fixedStickyContainer/fixedStickyContainer.component';

const spreadsheetDisabledDataEditorStyle = css({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
});

const alertStyle = css({
  width: 300,
});

type SpreadsheetDataEditorDisabledProps = Readonly<{
  onClose: () => void;
}>;

export const SpreadsheetDataEditorDisabledComponent: FC<PropsWithChildren<SpreadsheetDataEditorDisabledProps>> = (props) => {
  const [triggerRef, setTriggerRef] = useHookWithRefCallback<HTMLDivElement>();
  const [portalEl, setPortalEl] = useHookWithRefCallback<HTMLElement>();
  const { onClose } = props;
  const childrenRef = useRef<HTMLDivElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!portalEl) {
      return;
    }

    // Prevent click/mousedown events inside the portal from bubbling to window,
    // which would trigger react-datasheet's global close handler.
    const stopPropagation = (e: Event) => e.stopPropagation();
    portalEl.addEventListener('click', stopPropagation);
    portalEl.addEventListener('mousedown', stopPropagation);
    return () => {
      portalEl.removeEventListener('click', stopPropagation);
      portalEl.removeEventListener('mousedown', stopPropagation);
    };
  }, [portalEl]);

  useEffect(() => {
    const handleMouseDown = (event: MouseEvent) => {
      if (!childrenRef.current?.contains(event.target as Node)) {
        onCloseRef.current();
      }
    };

    window.addEventListener('mousedown', handleMouseDown);
    return () => window.removeEventListener('mousedown', handleMouseDown);
  }, []);

  return (
    <div
      css={spreadsheetDisabledDataEditorStyle}
      ref={setTriggerRef}
    >
      <FixedStickyContainerComponent
        ref={setPortalEl as Ref<HTMLElement>}
        isOpen
        onClose={onClose}
        triggerRef={triggerRef}
      >
        {() => (
          <AlertComponent
            type="danger"
            autoCloseTimeout={SPREADSHEET_ERROR_TIMEOUT}
            css={alertStyle}
            onClose={onClose}
          >
            <div ref={childrenRef}>{props.children}</div>
          </AlertComponent>
        )}
      </FixedStickyContainerComponent>
    </div>
  );
};
